import { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  createPreset,
  deletePreset,
  listPresets,
  setDefaultPreset,
  updatePreset,
} from '../api';
import type { ModelInfo, VideoGenConfig, VideoGenPreset } from '../types';

type FormState = VideoGenConfig & {
  seedText: string;
  offsetFrontText: string;
  offsetRearText: string;
  offsetLeftText: string;
  offsetRightText: string;
  heightOffsetFrontText: string;
  heightOffsetRearText: string;
  heightOffsetLeftText: string;
  heightOffsetRightText: string;
  maxWidthText: string;
  featherText: string;
  sampleStrideText: string;
  sampleStartText: string;
  sampleEndText: string;
};

const SHADOW_RESOLUTIONS = ['0.5K', '1K', '2K', '4K'];
const COMPOSE_MODES = ['with_background', 'no_background', 'off'];
const SHADOW_MODELS = ['nano-banana-2', 'gpt-image-2'];
const SHADOW_QUALITIES = ['low', 'medium', 'high'];

function toFormState(cfg: VideoGenConfig): FormState {
  return {
    ...cfg,
    seedText: String(cfg.seed),
    offsetFrontText: String(cfg.floor_y_offset_front),
    offsetRearText: String(cfg.floor_y_offset_rear),
    offsetLeftText: String(cfg.floor_y_offset_left),
    offsetRightText: String(cfg.floor_y_offset_right),
    heightOffsetFrontText: String(cfg.car_height_offset_front),
    heightOffsetRearText: String(cfg.car_height_offset_rear),
    heightOffsetLeftText: String(cfg.car_height_offset_left),
    heightOffsetRightText: String(cfg.car_height_offset_right),
    maxWidthText: String(cfg.car_max_width_frac),
    featherText: String(cfg.feather_px),
    sampleStrideText: String(cfg.sample_stride),
    sampleStartText: String(cfg.frames_to_sample_start),
    sampleEndText: String(cfg.frames_to_sample_end),
  };
}

function parseFrac(value: string, label: string, { allowZero = true } = {}): number {
  const n = Number.parseFloat(value);
  if (Number.isNaN(n) || n < 0 || n > 1 || (!allowZero && n <= 0)) {
    throw new Error(`${label} must be a number between ${allowZero ? '0' : '0 (exclusive)'} and 1`);
  }
  return n;
}

function parseSignedFrac(value: string, label: string, limit = 0.5): number {
  const n = Number.parseFloat(value);
  if (Number.isNaN(n) || n < -limit || n > limit) {
    throw new Error(`${label} must be a number between ${-limit} and ${limit}`);
  }
  return n;
}

function fromFormState(state: FormState): VideoGenConfig {
  const seed = Number.parseInt(state.seedText, 10);
  if (Number.isNaN(seed) || seed < 0) {
    throw new Error('Seed must be a non-negative integer');
  }
  const feather = Number.parseInt(state.featherText, 10);
  if (Number.isNaN(feather) || feather < 0) {
    throw new Error('Feather (px) must be a non-negative integer');
  }
  const sampleStride = Number.parseInt(state.sampleStrideText, 10);
  if (Number.isNaN(sampleStride) || sampleStride < 1 || sampleStride > 10) {
    throw new Error('Sample stride must be an integer between 1 and 10');
  }
  const {
    seedText: _seedText,
    offsetFrontText: _ofr,
    offsetRearText: _orr,
    offsetLeftText: _olt,
    offsetRightText: _ort,
    heightOffsetFrontText: _hfr,
    heightOffsetRearText: _hrr,
    heightOffsetLeftText: _hlt,
    heightOffsetRightText: _hrt,
    maxWidthText: _mw,
    featherText: _f,
    sampleStrideText: _ss,
    sampleStartText: _sst,
    sampleEndText: _set,
    ...rest
  } = state;
  return {
    ...rest,
    seed,
    sample_stride: sampleStride,
    frames_to_sample_start: parseFrac(state.sampleStartText, 'Frames to sample (start)'),
    frames_to_sample_end: parseFrac(state.sampleEndText, 'Frames to sample (end)'),
    floor_y_offset_front: parseSignedFrac(state.offsetFrontText, 'Front Y offset'),
    floor_y_offset_rear: parseSignedFrac(state.offsetRearText, 'Rear Y offset'),
    floor_y_offset_left: parseSignedFrac(state.offsetLeftText, 'Left Y offset'),
    floor_y_offset_right: parseSignedFrac(state.offsetRightText, 'Right Y offset'),
    car_height_offset_front: parseSignedFrac(state.heightOffsetFrontText, 'Front height offset'),
    car_height_offset_rear: parseSignedFrac(state.heightOffsetRearText, 'Rear height offset'),
    car_height_offset_left: parseSignedFrac(state.heightOffsetLeftText, 'Left height offset'),
    car_height_offset_right: parseSignedFrac(state.heightOffsetRightText, 'Right height offset'),
    car_max_width_frac: parseFrac(state.maxWidthText, 'Car max width fraction', { allowZero: false }),
    feather_px: feather,
  };
}

function findModel(models: ModelInfo[], id: string): ModelInfo | undefined {
  return models.find((m) => m.id === id);
}

/**
 * When switching models, prior values for `duration` / `resolution` /
 * `aspect_ratio` may not be valid for the new model. Keep what's still
 * valid; otherwise fall back to the new model's defaults. For models that
 * don't expose a knob at all (`supports.resolution === false` or
 * `supports.aspect_ratio === false`) we leave the previously stored value
 * untouched — it isn't sent to fal anyway.
 */
function reconcileForModel(form: FormState, target: ModelInfo): FormState {
  return {
    ...form,
    model: target.id,
    duration: target.choices.duration.includes(form.duration) ? form.duration : target.defaults.duration,
    resolution: target.supports.resolution
      ? target.choices.resolution.includes(form.resolution)
        ? form.resolution
        : target.defaults.resolution
      : form.resolution,
    aspect_ratio: target.supports.aspect_ratio
      ? target.choices.aspect_ratio.includes(form.aspect_ratio)
        ? form.aspect_ratio
        : target.defaults.aspect_ratio
      : form.aspect_ratio,
  };
}

export function VideoSettingsPage() {
  const [models, setModels] = useState<ModelInfo[]>([]);
  const [presets, setPresets] = useState<VideoGenPreset[]>([]);
  const [defaultId, setDefaultId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listPresets()
      .then((list) => {
        if (cancelled) return;
        setModels(list.models);
        setPresets(list.presets);
        setDefaultId(list.default_id);
        const initial = list.presets.find((p) => p.id === list.default_id) ?? list.presets[0];
        if (initial) {
          setSelectedId(initial.id);
          setForm(toFormState(initial.config));
        }
        setError(null);
      })
      .catch((ex) => {
        if (!cancelled) setError(ex instanceof Error ? ex.message : String(ex));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentModel = useMemo<ModelInfo | undefined>(() => {
    if (!form) return undefined;
    return findModel(models, form.model);
  }, [models, form]);

  const selectedPreset = useMemo<VideoGenPreset | undefined>(
    () => presets.find((p) => p.id === selectedId),
    [presets, selectedId],
  );

  const handleModelChange = (modelId: string) => {
    if (!form) return;
    const target = findModel(models, modelId);
    if (!target) return;
    setForm(reconcileForModel(form, target));
  };

  const handleSelectPreset = (id: string) => {
    const preset = presets.find((p) => p.id === id);
    if (!preset) return;
    setSelectedId(id);
    setForm(toFormState(preset.config));
    setSavedAt(null);
    setError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form || !selectedId) return;
    setSaving(true);
    setError(null);
    try {
      const payload = fromFormState(form);
      const updated = await updatePreset(selectedId, { config: payload });
      setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      setForm(toFormState(updated.config));
      setSavedAt(Date.now());
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => {
    if (!selectedPreset) return;
    setForm(toFormState(selectedPreset.config));
    setSavedAt(null);
    setError(null);
  };

  const handleNewPreset = async () => {
    const name = window.prompt('Name for the new preset?', 'New preset');
    if (!name || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createPreset(name.trim());
      setPresets((prev) => [...prev, created]);
      setSelectedId(created.id);
      setForm(toFormState(created.config));
      setSavedAt(null);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleDuplicatePreset = async () => {
    if (!form || !selectedPreset) return;
    const name = window.prompt('Name for the duplicated preset?', `${selectedPreset.name} copy`);
    if (!name || !name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const created = await createPreset(name.trim(), fromFormState(form));
      setPresets((prev) => [...prev, created]);
      setSelectedId(created.id);
      setForm(toFormState(created.config));
      setSavedAt(null);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleRenamePreset = async () => {
    if (!selectedId || !selectedPreset) return;
    const name = window.prompt('Rename preset', selectedPreset.name);
    if (!name || !name.trim() || name.trim() === selectedPreset.name) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await updatePreset(selectedId, { name: name.trim() });
      setPresets((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePreset = async () => {
    if (!selectedId || !selectedPreset) return;
    if (selectedPreset.builtin) {
      setError('Built-in presets cannot be deleted.');
      return;
    }
    if (presets.length <= 1) {
      setError('Cannot delete the only preset.');
      return;
    }
    if (!window.confirm(`Delete preset "${selectedPreset.name}"? This cannot be undone.`)) return;
    setSaving(true);
    setError(null);
    try {
      const list = await deletePreset(selectedId);
      setPresets(list.presets);
      setDefaultId(list.default_id);
      const next = list.presets.find((p) => p.id === list.default_id) ?? list.presets[0];
      if (next) {
        setSelectedId(next.id);
        setForm(toFormState(next.config));
      }
      setSavedAt(null);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async () => {
    if (!selectedId) return;
    setSaving(true);
    setError(null);
    try {
      const list = await setDefaultPreset(selectedId);
      setDefaultId(list.default_id);
      setPresets(list.presets);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form || !currentModel || !selectedId) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-primary-900">Video Settings</h1>
        <div className="text-sm text-primary-400">Loading…</div>
      </div>
    );
  }

  const labelClass = 'block text-sm font-medium text-primary-800 mb-1';
  const inputClass =
    'w-full px-3 py-2 rounded-lg border border-primary-200 bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm disabled:opacity-60';
  const secondaryBtn =
    'px-3 py-2 rounded-lg border border-primary-200 bg-white text-sm text-primary-700 hover:bg-primary-50 disabled:opacity-50';

  return (
    <div className="space-y-4 max-w-3xl">
      <div>
        <h1 className="text-2xl font-semibold text-primary-900">Video Settings</h1>
        <p className="text-sm text-primary-500 mt-1">
          Each <span className="font-medium">preset</span> is a full copy of the video-generation
          parameters (e.g. a different background type). A scan uses the <span className="font-medium">default</span>{' '}
          preset unless overridden when generating. Changes take effect on the next scan submitted
          (jobs already running keep the snapshot they started with).
        </p>
      </div>

      <div className="bg-white border border-primary-100 rounded-2xl p-4 shadow-card space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className={labelClass}>Preset</label>
            <select
              value={selectedId}
              onChange={(e) => handleSelectPreset(e.target.value)}
              disabled={saving}
              className={inputClass}
            >
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.builtin ? ' (built-in)' : ''}
                  {p.id === defaultId ? ' (default)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={handleNewPreset} disabled={saving} className={secondaryBtn}>
              New
            </button>
            <button type="button" onClick={handleDuplicatePreset} disabled={saving} className={secondaryBtn}>
              Duplicate
            </button>
            <button type="button" onClick={handleRenamePreset} disabled={saving} className={secondaryBtn}>
              Rename
            </button>
            <button
              type="button"
              onClick={handleSetDefault}
              disabled={saving || selectedId === defaultId}
              className={secondaryBtn}
            >
              Set default
            </button>
            <button
              type="button"
              onClick={handleDeletePreset}
              disabled={saving || presets.length <= 1 || !!selectedPreset?.builtin}
              title={selectedPreset?.builtin ? 'Built-in presets cannot be deleted' : undefined}
              className="px-3 py-2 rounded-lg border border-red-200 bg-white text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
        <p className="text-xs text-primary-500">
          Editing preset <span className="font-medium">{selectedPreset?.name}</span>
          {selectedId === defaultId ? ' (this is the default used for new scans)' : ''}.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}
      {savedAt && !error && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
          Saved at {new Date(savedAt).toLocaleTimeString()}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5 bg-white border border-primary-100 rounded-2xl p-6 shadow-card">
        <div>
          <label className={labelClass}>Model</label>
          <select
            value={form.model}
            onChange={(e) => handleModelChange(e.target.value)}
            disabled={saving}
            className={inputClass}
          >
            {models.map((m) => (
              <option key={m.id} value={m.id}>
                {m.display_name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-primary-500">
            <span className="font-mono">{currentModel.endpoint}</span>
            {currentModel.description && <> — {currentModel.description}</>}
          </p>
        </div>

        <div>
          <label className={labelClass}>
            Prompt {form.compose_mode === 'no_background' ? '(no background mode)' : '(with background / off modes)'}
          </label>
          {form.compose_mode === 'no_background' ? (
            <textarea
              value={form.prompt_no_background}
              onChange={(e) => setForm({ ...form, prompt_no_background: e.target.value })}
              disabled={saving}
              rows={6}
              className={`${inputClass} font-mono`}
            />
          ) : (
            <textarea
              value={form.prompt}
              onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              disabled={saving}
              rows={6}
              className={`${inputClass} font-mono`}
            />
          )}
          <p className="mt-1 text-xs text-primary-500">
            The positive prompt follows the preprocessing Mode selected below.{' '}
            <code>no_background</code> places the car on a blank white canvas, so its prompt must not
            mention a floor, ground, turntable or shadows. <code>with_background</code> and{' '}
            <code>off</code> share the floor-aware prompt. The negative prompt is shared across modes.
          </p>
        </div>

        {currentModel.supports.negative_prompt && (
          <div>
            <label className={labelClass}>Negative prompt</label>
            <textarea
              value={form.negative_prompt}
              onChange={(e) => setForm({ ...form, negative_prompt: e.target.value })}
              disabled={saving}
              rows={6}
              className={`${inputClass} font-mono`}
            />
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Duration (per pair)</label>
            <select
              value={form.duration}
              onChange={(e) => setForm({ ...form, duration: e.target.value })}
              disabled={saving}
              className={inputClass}
            >
              {currentModel.choices.duration.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>
          </div>
          {currentModel.supports.resolution && (
            <div>
              <label className={labelClass}>Resolution</label>
              <select
                value={form.resolution}
                onChange={(e) => setForm({ ...form, resolution: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {currentModel.choices.resolution.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>
          )}
          {currentModel.supports.aspect_ratio && (
            <div>
              <label className={labelClass}>Aspect ratio</label>
              <select
                value={form.aspect_ratio}
                onChange={(e) => setForm({ ...form, aspect_ratio: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {currentModel.choices.aspect_ratio.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Seed</label>
            <input
              type="number"
              min={0}
              step={1}
              value={form.seedText}
              onChange={(e) => setForm({ ...form, seedText: e.target.value })}
              disabled={saving}
              className={`${inputClass} font-mono`}
            />
          </div>
          {currentModel.supports.generate_audio && (
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={form.generate_audio}
                onChange={(e) => setForm({ ...form, generate_audio: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Generate audio</span>
            </label>
          )}
          {currentModel.supports.auto_fix && (
            <label className="flex items-end gap-2 pb-2">
              <input
                type="checkbox"
                checked={form.auto_fix}
                onChange={(e) => setForm({ ...form, auto_fix: e.target.checked })}
                disabled={saving}
                className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-primary-800">Auto-fix</span>
            </label>
          )}
        </div>

        <div className="pt-4 border-t border-primary-100 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-primary-900">Motion resampling</h2>
            <p className="mt-1 text-xs text-primary-500">
              The 360 is stitched from four sub-clips, so the model eases the car to a stop at the
              end of each one — producing a periodic "slow down" at every seam. Frames near a clip's
              ends are dropped to speed those regions back up; the decimation ramps in{' '}
              <span className="font-medium">gradually</span> (none at the boundary → up to the stride
              at the very end) so the speed-up is smooth with no jump. A stride of 1 disables it.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Sample stride</label>
              <input
                type="number"
                min={1}
                max={10}
                step={1}
                value={form.sampleStrideText}
                onChange={(e) => setForm({ ...form, sampleStrideText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">Max stride at the very end (ramped in).</p>
            </div>
            <div>
              <label className={labelClass}>Sample start (frac)</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.sampleStartText}
                onChange={(e) => setForm({ ...form, sampleStartText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">Fraction of frames from the start.</p>
            </div>
            <div>
              <label className={labelClass}>Sample end (frac)</label>
              <input
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={form.sampleEndText}
                onChange={(e) => setForm({ ...form, sampleEndText: e.target.value })}
                disabled={saving}
                className={`${inputClass} font-mono`}
              />
              <p className="mt-1 text-xs text-primary-500">Fraction of frames from the end (e.g. 0.1).</p>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-primary-100 space-y-4">
          <div>
            <h2 className="text-sm font-semibold text-primary-900">Keyframe preprocessing</h2>
            <p className="mt-1 text-xs text-primary-500">
              {form.compose_mode === 'with_background' && (
                <>
                  <strong>with background</strong>: matte the car, align it to the four poses'
                  median size/position (clamped so it never clips), match exposure, then composite
                  only the car onto the Background URL plate — the background is fully replaced. A
                  model adds a realistic floor shadow, confined to darkening so the car/background
                  stay intact. The per-pose Y and height offsets are applied so sides and front/rear
                  can sit a little differently.
                </>
              )}
              {form.compose_mode === 'no_background' && (
                <>
                  <strong>no background</strong>: matte the car, auto-calibrate its size/position to
                  the four poses' median, and composite it onto a blank white canvas — the
                  background is removed entirely. No plate, no shadow, no per-pose offsets.
                </>
              )}
              {form.compose_mode === 'off' && (
                <>
                  <strong>off</strong>: preprocessing disabled. The four poses pass straight through
                  to the video model.
                </>
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <label className={labelClass}>Mode</label>
              <select
                value={form.compose_mode}
                onChange={(e) => setForm({ ...form, compose_mode: e.target.value })}
                disabled={saving}
                className={inputClass}
              >
                {COMPOSE_MODES.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            {form.compose_mode !== 'off' && (
              <div>
                <label className={labelClass}>Max width (frac)</label>
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.02}
                  value={form.maxWidthText}
                  onChange={(e) => setForm({ ...form, maxWidthText: e.target.value })}
                  disabled={saving}
                  className={`${inputClass} font-mono`}
                />
              </div>
            )}
            {form.compose_mode !== 'off' && (
              <label className="flex items-center gap-2 pb-2 sm:col-span-2 sm:self-end">
                <input
                  type="checkbox"
                  checked={form.normalize_exposure}
                  onChange={(e) => setForm({ ...form, normalize_exposure: e.target.checked })}
                  disabled={saving}
                  className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-primary-800">Normalize exposure</span>
              </label>
            )}
          </div>

          {form.compose_mode === 'with_background' && (
            <>
              <div>
                <p className="mb-2 text-xs text-primary-500">
                  Per-pose vertical nudge (fraction of height, −0.5…0.5), added on top of the auto
                  floor line. Positive moves the car down. Each pose is independent, so sides
                  (<code>left</code>/<code>right</code>) and fronts (<code>front</code>/
                  <code>rear</code>) can sit a little differently. <code>0</code> = no shift.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className={labelClass}>Front Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetFrontText}
                      onChange={(e) => setForm({ ...form, offsetFrontText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Rear Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetRearText}
                      onChange={(e) => setForm({ ...form, offsetRearText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Left Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetLeftText}
                      onChange={(e) => setForm({ ...form, offsetLeftText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Right Y offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.offsetRightText}
                      onChange={(e) => setForm({ ...form, offsetRightText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs text-primary-500">
                  Per-pose height nudge (fraction of height, −0.5…0.5), added on top of the auto
                  median car height. Positive enlarges the car. Pair this with the Y offset so the
                  physical effect (a pose nudged forward/down also looks a touch bigger) makes sense.
                  <code>0</code> = no change.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div>
                    <label className={labelClass}>Front height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetFrontText}
                      onChange={(e) => setForm({ ...form, heightOffsetFrontText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Rear height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetRearText}
                      onChange={(e) => setForm({ ...form, heightOffsetRearText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Left height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetLeftText}
                      onChange={(e) => setForm({ ...form, heightOffsetLeftText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Right height offset</label>
                    <input
                      type="number"
                      min={-0.5}
                      max={0.5}
                      step={0.01}
                      value={form.heightOffsetRightText}
                      onChange={(e) => setForm({ ...form, heightOffsetRightText: e.target.value })}
                      disabled={saving}
                      className={`${inputClass} font-mono`}
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 items-end">
                <label className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    checked={form.generative_shadow}
                    onChange={(e) => setForm({ ...form, generative_shadow: e.target.checked })}
                    disabled={saving}
                    className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-primary-800">Generative shadow</span>
                </label>
                <label className="flex items-center gap-2 pb-2">
                  <input
                    type="checkbox"
                    checked={form.add_contact_shadow}
                    onChange={(e) => setForm({ ...form, add_contact_shadow: e.target.checked })}
                    disabled={saving}
                    className="h-4 w-4 rounded border-primary-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-primary-800">Synthetic shadow</span>
                </label>
                <div>
                  <label className={labelClass}>Edge feather (px)</label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={form.featherText}
                    onChange={(e) => setForm({ ...form, featherText: e.target.value })}
                    disabled={saving}
                    className={`${inputClass} font-mono`}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelClass}>Shadow model</label>
                  <select
                    value={form.shadow_model}
                    onChange={(e) => setForm({ ...form, shadow_model: e.target.value })}
                    disabled={saving}
                    className={inputClass}
                  >
                    {SHADOW_MODELS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>gpt-image-2 quality</label>
                  <select
                    value={form.shadow_gpt_quality}
                    onChange={(e) => setForm({ ...form, shadow_gpt_quality: e.target.value })}
                    disabled={saving || form.shadow_model !== 'gpt-image-2'}
                    className={inputClass}
                  >
                    {SHADOW_QUALITIES.map((q) => (
                      <option key={q} value={q}>
                        {q}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Shadow resolution</label>
                  <select
                    value={form.shadow_resolution}
                    onChange={(e) => setForm({ ...form, shadow_resolution: e.target.value })}
                    disabled={saving || form.shadow_model !== 'nano-banana-2'}
                    className={inputClass}
                  >
                    {SHADOW_RESOLUTIONS.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className={labelClass}>Shadow prompt (generative shadow)</label>
                <textarea
                  value={form.shadow_prompt}
                  onChange={(e) => setForm({ ...form, shadow_prompt: e.target.value })}
                  disabled={saving}
                  rows={3}
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  When Generative shadow is on, the car is composited onto the plate and the chosen
                  model (nano-banana-2 or gpt-image-2) adds a realistic floor shadow — confined to
                  darkening below the car, so the car and background are never altered. Falls back to
                  Synthetic shadow if it fails.
                </p>
              </div>

              <div>
                <label className={labelClass}>Background URL (plate)</label>
                <input
                  type="url"
                  value={form.background_url}
                  onChange={(e) => setForm({ ...form, background_url: e.target.value })}
                  disabled={saving}
                  placeholder="https://… (clean full-frame studio incl. floor)"
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  The full background the car is composited onto (floor included). Required for this
                  mode.
                </p>
              </div>

              <div>
                <label className={labelClass}>Background URL — secondary (plate)</label>
                <input
                  type="url"
                  value={form.background_url_secondary}
                  onChange={(e) => setForm({ ...form, background_url_secondary: e.target.value })}
                  disabled={saving}
                  placeholder="https://… (optional second plate)"
                  className={`${inputClass} font-mono`}
                />
                <p className="mt-1 text-xs text-primary-500">
                  Optional. When set, the four poses are 2-coloured so every adjacent video pair
                  mixes the two plates: <code>front</code>/<code>rear</code> use the primary,{' '}
                  <code>left</code>/<code>right</code> use the secondary. Each pose keeps its plate
                  across all segments. Empty ⇒ all poses use the primary.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-primary-100">
          <button
            type="button"
            onClick={handleRevert}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-60"
          >
            Revert changes
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-brand-gradient shadow-card hover:shadow-card-hover hover:opacity-95 transition-all disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
